import { defineComponent, h, type PropType } from "vue";
import PersonLink from "@/components/shared/PersonLink.vue";

type Maybe<T> = T | null | undefined;

/**
 * Header `renderer` that shows a cell as the shared {@link PersonLink}. `personId` reads the id off
 * the row; `name` defaults to the cell value (after any `computeValue`).
 *
 * @example { key: "personName", title: "Name", renderer: personLinkRenderer((row) => row.personId?.personId) }
 */
export function personLinkRenderer<T>(
  personId: (item: T) => Maybe<number>,
  name?: (item: T) => Maybe<string>,
  fallback?: string,
) {
  return defineComponent({
    name: "PersonLinkCell",
    props: {
      // Loosely typed so table slots (which pass readonly/deep-typed rows) can bind it directly.
      item: { type: Object as PropType<any>, required: true },
      value: { type: null as unknown as PropType<unknown>, default: null },
    },
    setup(props) {
      return () =>
        h(PersonLink, {
          personId: personId(props.item as T) ?? null,
          name: name
            ? (name(props.item as T) ?? null)
            : props.value == null
              ? null
              : String(props.value),
          fallback,
        });
    },
  });
}

/** Headers that declare a `renderer`, for tables that render cells through their own slots. */
export function rendererHeaders<H extends { key: string; renderer?: unknown }>(
  headers: readonly H[],
): (H & { renderer: NonNullable<H["renderer"]> })[] {
  return headers.filter(
    (header): header is H & { renderer: NonNullable<H["renderer"]> } =>
      header.renderer != null,
  );
}
